import { Component, createResource, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Text } from '../../ui-components/text/text';

export type TermsAndConditionsProps = {
    contentType: 'privacy' | 'marketing';
};

/**
 * Fetches terms content dynamically from the API.
 * This ensures content is always fresh, not cached at publish time.
 */
const fetchTermsContent = async (
    siteId: string,
    contentType: 'privacy' | 'marketing'
): Promise<string | null> => {
    try {
        const response = await fetch('/api-client/terms-and-conditions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ siteId }),
        });

        const data = await response.json();

        if (data.success && data.termsAndConditions) {
            const content = contentType === 'marketing'
                ? data.termsAndConditions.marketingConsent?.content
                : data.termsAndConditions.privacyPolicy?.content;

            return content || null;
        }

        return null;
    } catch (error) {
        console.error('[TermsAndConditions] Error fetching content:', error);
        return null;
    }
};

/**
 * Terms & Conditions Block
 *
 * Renders privacy policy or marketing consent content.
 * Content is fetched dynamically from the API to ensure it's always up-to-date,
 * rather than being cached at page publish time.
 */
export const TermsAndConditions: Component<TermsAndConditionsProps> = (props) => {
    const { siteInfo, localize } = useContext(AppContext);

    const [content] = createResource(
        () => ({ siteId: siteInfo?.siteId, contentType: props.contentType }),
        async ({ siteId, contentType }) => {
            if (!siteId) return null;
            return fetchTermsContent(siteId, contentType);
        }
    );

    return (
        <Show
            when={!content.loading && content()}
            fallback={
                <div class="flex flex-col items-center justify-center py-16 gap-4">
                    <div class="w-8 h-8 border-4 border-grey-200 border-t-grey-500 rounded-full animate-spin" />
                    <Text fontSize="small" color="grey500">
                        {localize('loading', 'Loading...')}
                    </Text>
                </div>
            }
        >
            <div
                class="max-w-none text-normal leading-relaxed
                    [&_h1]:text-large [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 first:[&_h1]:mt-0
                    [&_h2]:text-normal [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5 first:[&_h2]:mt-0
                    [&_h3]:text-normal [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-4 first:[&_h3]:mt-0
                    [&_p]:mb-4
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                    [&_li]:mb-1
                    [&_strong]:font-bold
                    [&_a]:text-primary [&_a]:underline [&_a:hover]:text-grey-500"
                innerHTML={content()}
            />
        </Show>
    );
};

TermsAndConditions.parseProps = (atts: any) => {
    return {
        contentType: atts.contentType || 'privacy',
    };
};
